import dayjs, { Dayjs } from "dayjs";

// The E-Tax date range is carried between the dashboard and its detail / history
// pages via query params so each screen is a real, linkable route (instead of an
// in-memory modal). Keys mirror the API query params (date_from / date_to).

// The API accepts strictly `YYYY-MM-DD`; anything else is rejected with a 400 and the
// screen falls back to its empty state, which reads as "no data for this range".
// A MUI DatePicker emits a value on every section edit, so a half-typed date lands in
// state as an INVALID Dayjs — truthy, but `.format()` returns the literal "Invalid Date".
// Serializers therefore check `isValid()` and treat an unfinished date as "no bound".
export const formatDateParam = (date: Dayjs | null | undefined): string | null =>
  date?.isValid() ? date.format("YYYY-MM-DD") : null;

export const buildDateRangeSearch = (
  dateFrom: Dayjs | null,
  dateTo: Dayjs | null,
): string => {
  const params = new URLSearchParams();
  const from = formatDateParam(dateFrom);
  const to = formatDateParam(dateTo);
  if (from) params.append("date_from", from);
  if (to) params.append("date_to", to);
  const search = params.toString();
  return search ? `?${search}` : "";
};

export const parseDateParam = (value: string | null): Dayjs | null => {
  if (!value) return null;
  const date = dayjs(value);
  return date.isValid() ? date : null;
};

/**
 * Default range every E-Tax screen opens on: the 1st of the current month → today.
 *
 * Opening with no range at all made the API return every candidate document ever
 * recorded, while the heading claimed the current month — so an empty month read as
 * "the filter is broken" instead of "this month has nothing yet". Anchoring on the
 * current month keeps what is shown and what is labelled in agreement from the first
 * paint, and is derived from the clock, never hardcoded to a specific month.
 */
export const getDefaultDateRange = (): { from: Dayjs; to: Dayjs } => {
  const today = dayjs();
  return { from: today.startOf("month"), to: today };
};

/**
 * Seed a picker from the URL. Each bound falls back on its own, so a link carrying only one
 * of them still opens on a complete range — the picker has no "no bound" state to fall into.
 */
export const resolveInitialDateRange = (
  dateFromParam: string | null,
  dateToParam: string | null,
): { from: Dayjs; to: Dayjs } => {
  const fallback = getDefaultDateRange();
  const from = parseDateParam(dateFromParam) ?? fallback.from;
  const to = parseDateParam(dateToParam) ?? fallback.to;
  // A hand-edited link with the bounds the wrong way round means the same period; swap it
  // rather than query a range that can never match a row.
  return isDateRangeInvalid(from, to) ? { from: to, to: from } : { from, to };
};

/**
 * Read an optional range from the URL for screens that accept "no bound" (details, history,
 * batch detail). Inverted bounds are swapped, like {@link resolveInitialDateRange}.
 */
export const resolveDateRangeParams = (
  dateFromParam: string | null,
  dateToParam: string | null,
): { from: Dayjs | null; to: Dayjs | null } => {
  const from = parseDateParam(dateFromParam);
  const to = parseDateParam(dateToParam);
  return isDateRangeInvalid(from, to) ? { from: to, to: from } : { from, to };
};

/**
 * `true` when the start date sits after the end date.
 *
 * The picker makes an inverted range unreachable through the UI, so this only
 * catches one that arrives via the URL (a hand-edited or stale deep link). Such a range
 * can never match a row, so callers skip the request rather than render an empty result
 * that looks identical to "this period has no documents".
 */
export const isDateRangeInvalid = (
  dateFrom: Dayjs | null | undefined,
  dateTo: Dayjs | null | undefined,
): boolean =>
  Boolean(
    dateFrom?.isValid() &&
      dateTo?.isValid() &&
      dateFrom.startOf("day").isAfter(dateTo.startOf("day")),
  );

